import React from "react"
import loadable from "@loadable/component"
import { useRoutes } from "react-router-dom"

import App from "@containers/App"

import { ErrorBoundary } from "@wrappers/ErrorBoundary"

import Skeleton from "@components/Skeleton/Skeleton"

import SavingsSkeleton from "@pages/SavingsPage/SavingsPage.skeleton"
import InsightsSupplement from "../containers/ABExperiments/HealthRecords/InsightsSupplement/InsightsSupplement"

const SavingsPage = loadable(() => import("@pages/SavingsPage/SavingsPage.js"), {
    fallback: <SavingsSkeleton></SavingsSkeleton>,
})

import CartSkeleton from "@pages/CartPage/CartPage.skeleton"
const CartPage = loadable(() => import("@pages/CartPage/CartPage.js"), {
    fallback: <CartSkeleton />,
})

import AddressSkeleton from "@pages/AddressPage/AddressPage.skeleton"
const AddressPage = loadable(() => import("@pages/AddressPage/AddressPage.js"), {
    fallback: <AddressSkeleton />,
})

import PatientSlotSkeleton from "@pages/SlotPage/SlotPage.skeleton"
const PatientSlotPage = loadable(() => import("@pages/SlotPage/SlotPage.js"), {
    fallback: <PatientSlotSkeleton />,
})

import PrescriptionSkeleton from "@pages/PrescriptionPage/PrescriptionPage.skeleton"
const PrescriptionPage = loadable(() => import("@pages/PrescriptionPage/PrescriptionPage.js"), {
    fallback: <PrescriptionSkeleton />,
})

import DioAttachPrescriptionSkeleton from "@pages/DioAttachPrescription/DioAttachPrescription.skeleton"
const DioAttachPrescription = loadable(
    () => import("@pages/DioAttachPrescription/DioAttachPrescription.js"),
    {
        fallback: <DioAttachPrescriptionSkeleton />,
    }
)

import DioOrderPreviewSkeleton from "../pages/DioOrderPreview/DioOrderPreview.skeleton"
const DioOrderPreview = loadable(() => import("@pages/DioOrderPreview/DioOrderPreview.js"), {
    fallback: <DioOrderPreviewSkeleton />,
})

import DioRxProcessingSkeleton from "../pages/DioRxProcessing/DioRxProcessing.skeleton"
const DioRxProcessing = loadable(() => import("@pages/DioRxProcessing/DioRxProcessing.js"), {
    fallback: <DioRxProcessingSkeleton />,
})

import DioAddressSkeleton from "@pages/DioAddressPage/DioAddressPage.skeleton"
const DioAddressPage = loadable(() => import("@pages/DioAddressPage/DioAddressPage.js"), {
    fallback: <DioAddressSkeleton />,
})

import LabsBookingSkeleton from "@pages/Corporate/LabsBookingPage/LabsBookingPage.skeleton"
const LabsBookingPage = loadable(() => import("@pages/Corporate/LabsBookingPage/LabsBookingPage.js"), {
    fallback: <LabsBookingSkeleton></LabsBookingSkeleton>,
})

const PatientSelection = loadable(() => import("@pages/Corporate/LabsBookingPage/PatientSelection"), {
    fallback: <div></div>,
})
import DashboardSkeleton from "@pages/Corporate/CorporateDashboard/CorporateDashboard.skeleton"
const CorporateDashboard = loadable(() => import("@pages/Corporate/CorporateDashboard/index.js"), {
    fallback: <DashboardSkeleton />,
})
import CashlessBenefitSkeleton from "@pages/Corporate/TransactionHistoryPage/TransactionHistoryPage.skeleton"
const CashlessBenefitContainer = loadable(
    () => import("@pages/Corporate/CorporateDashboard/Widgets/CashlessBenefitContainer.js"),
    {
        fallback: <CashlessBenefitSkeleton />,
    }
)
const Ekincare = loadable(() => import("@pages/Corporate/CorporateDashboard/Widgets/Ekincare/Ekincare.js"), {
    fallback: <div></div>,
})

import TransactionHistorySkeleton from "@pages/Corporate/TransactionHistoryPage/TransactionHistoryPage.skeleton"
const TransactionHistoryPage = loadable(
    () => import("@pages/Corporate/TransactionHistoryPage/TransactionHistoryPage"),
    {
        fallback: <TransactionHistorySkeleton />,
    }
)

const PackageSelection = loadable(() => import("@pages/Corporate/LabsBookingPage/PackageSelection"), {
    fallback: <div></div>,
})
const TestDetails = loadable(() => import("@pages/Corporate/LabsBookingPage/TestDetails"), {
    fallback: <div></div>,
})
const BookingReview = loadable(() => import("@pages/Corporate/LabsBookingPage/BookingReview"), {
    fallback: <div></div>,
})

const BookingSuccessPage = loadable(
    () => import("@pages/Corporate/BookingSuccessPage/BookingSuccessPage.js"),
    {
        fallback: <div></div>,
    }
)

import LabsDetailsSkeleton from "@pages/Labs/LabDetailsPage/LabDetailsPage.skeleton"
const LabDetailsPage = loadable(() => import("@pages/Labs/LabDetailsPage/LabDetailsPage.js"), {
    fallback: <LabsDetailsSkeleton />,
})
import ShipmentSkeleton from "@pages/ShipmentPage/ShipmentPage.skeleton"
const ShipmentPage = loadable(() => import("@pages/ShipmentPage/ShipmentPage"), {
    fallback: <ShipmentSkeleton />,
})

import CartSummarySkeleton from "@pages/ShipmentPage/ShipmentPage.skeleton"
const CartSummaryPage = loadable(() => import("@pages/CartSummaryPage/CartSummaryPage"), {
    fallback: <CartSummarySkeleton />,
})

import CartPaymentSkeleton from "@pages/CartPayment/CartPayment.skeleton"
const CartPayment = loadable(() => import("@pages/CartPayment/CartPayment.js"), {
    fallback: <CartPaymentSkeleton />,
})

import { orderSuccess as orderSuccessSkeletonConfig } from "@pages/OrderSuccess/OrderSuccess.skeleton"
import BosSuccessPage from "../containers/ABExperiments/HealthRecords/BosSuccessPage/BosSuccessPage"
const OrderSuccessPage = loadable(() => import("@pages/OrderSuccess/OrderSuccess"), {
    fallback: <Skeleton config={orderSuccessSkeletonConfig} />,
})

const RechargeWallet = loadable(() => import("@pages/CartPayment/RechargeWallet.js"))

const ErrorPage = loadable(() => import("@pages/ErrorPage/ErrorPage.js"))

const ErrorComponent = () => {
    return <>Error...</>
}

import LabsInventorySkeleton from "@pages/Labs/InventoryPage/InventoryPage.skeleton"
const LabsInventoryPage = loadable(() => import("@pages/Labs/InventoryPage/InventoryPage.js"), {
    fallback: <LabsInventorySkeleton></LabsInventorySkeleton>,
})

import PartnerOfferSkeleton from "@pages/PartnerOffersPage/PartnerOffersPage.skeleton.js"
const PartnerOffersPage = loadable(() => import("@pages/PartnerOffersPage/PartnerOffersPage.js"), {
    fallback: <PartnerOfferSkeleton></PartnerOfferSkeleton>,
})

import OfferDetailSkeleton from "@pages/OfferDetailPage/OfferDetailPage.skeleton"
const OfferDetailPage = loadable(() => import("@pages/OfferDetailPage/OfferDetailPage.js"), {
    fallback: <OfferDetailSkeleton></OfferDetailSkeleton>,
})

const SelfServeContainer = loadable(() => import("@pages/Corporate/SelfServe"))
const SelfServeLogin = loadable(() => import("@pages/Corporate/SelfServe/Login"), {
    fallback: <div></div>,
})
const SelfServeBookingSuccess = loadable(() => import("@pages/Corporate/SelfServe/BookingSuccess"), {
    fallback: <div></div>,
})

import SelfServeLeadPageConfig from "@pages/Corporate/SelfServe/LeadPage/LeadPage.skeleton"
const SelfServeLeadPage = loadable(() => import("@pages/Corporate/SelfServe/LeadPage"), {
    fallback: <Skeleton config={SelfServeLeadPageConfig} />,
})

const HealthRecordsPage = loadable(() => import("@pages/HealthRecordsPage/HealthRecordsPage"), {
    fallback: <div></div>,
})

const CorporateLandingPageContainer = loadable(() => import("@pages/Corporate/CorporateHomePage"), {
    fallback: <div></div>,
})

const CorporateLandingPage = loadable(() => import("@pages/Corporate/CorporateHomePage/LandingPage.js"), {
    fallback: <div></div>,
})

const BusinessVerticalPage = loadable(
    () => import("@pages/Corporate/CorporateHomePage/BusinessVerticalPage.js"),
    {
        fallback: <div></div>,
    }
)

const VaccinationPageContainer = loadable(() => import("@pages/Corporate/VaccinationPage"), {
    fallback: <div></div>,
})

const VaccinationHomePage = loadable(() => import("@pages/Corporate/VaccinationPage/VaccinationHomePage"), {
    fallback: <div></div>,
})

const CampaignLandingPageContainer = loadable(() => import("@pages/Campaigns"), {
    fallback: <div></div>,
})

const TrustLandingPage = loadable(() => import("@pages/Campaigns/Trust/LandingPage"), {
    fallback: <div></div>,
})

const LeadGenPage = loadable(() => import("@pages/LeadGenPage/LeadGenPage"), {
    fallback: <div></div>,
})

const routes = [
    {
        path: "/partner-offers",
        component: PartnerOffersPage,
        end: true,
    },
    {
        path: "/offer-detail/:partner_id/:partnership_benefit_id",
        component: OfferDetailPage,
        end: true,
    },
    {
        path: "labs/:city/search",
        component: LabsInventoryPage,
        end: true,
    },
    {
        path: "/subscription-plan/myplan",
        component: SavingsPage,
        end: true,
    },
    {
        path: "/cart",
        component: CartPage,
        end: true,
    },
    {
        path: "/labs/cart",
        component: CartPage,
        exact: true,
    },
    {
        path: "/cart/address-page",
        component: AddressPage,
        end: true,
    },
    {
        path: "/labs/cart/address-page",
        component: AddressPage,
        end: true,
    },
    {
        path: "/cart/prescription",
        component: PrescriptionPage,
        end: true,
    },
    {
        path: "/cart/select-shipment",
        component: ShipmentPage,
        end: true,
    },
    {
        path: "/cart/summary-page",
        component: CartSummaryPage,
        end: true,
    },
    {
        path: "/cart/payment",
        component: CartPayment,
        end: true,
    },
    {
        path: "/cart/payment/recharge",
        component: RechargeWallet,
        end: true,
    },
    {
        path: "/cart/checkout/success",
        end: true,
        component: OrderSuccessPage,
    },
    {
        path: "/order-payment/",
        component: CartPayment,
        end: true,
    },
    {
        path: "/done-in-one/prescription-order",
        component: DioAttachPrescription,
        end: true,
    },
    {
        path: "/done-in-one/rx-order-preview",
        component: DioOrderPreview,
        end: true,
    },
    {
        path: "/labs/:city/diagnostic-centers/:labName",
        component: LabDetailsPage,
        end: true,
    },
    {
        path: "/done-in-one/rx-order-processing",
        component: DioRxProcessing,
        end: true,
    },
    {
        path: "/insights-supplement/bos-package-success",
        end: true,
        component: BosSuccessPage,
    },
    {
        path: "/done-in-one/cart/address-page",
        component: DioAddressPage,
        end: true,
    },
    {
        path: "/corporate/labs/summary",
        component: BookingSuccessPage,
        end: true,
    },
    {
        path: "/labs/cart/patient-page",
        component: PatientSlotPage,
        end: true,
    },
    {
        path: "/corporate/:corporate_name",
        component: CorporateDashboard,
        end: true,
    },
    {
        path: "/corporate/widget/cashlessBenefit",
        component: CashlessBenefitContainer,
        end: true,
    },
    {
        path: "/corporate/widget/ekincare",
        component: Ekincare,
        end: true,
    },
    {
        path: "/corporate/wallet/transaction-history",
        end: true,
        component: TransactionHistoryPage,
    },
    {
        path: "/corporate/member-list",
        component: DioAttachPrescription,
        end: true,
    },
    {
        path: "/cart/payment/recharge",
        component: RechargeWallet,
        end: true,
    },
    {
        path: "/order-payment/",
        component: CartPayment,
        end: true,
    },
    {
        path: "/labs/cart/summary-page",
        component: CartSummaryPage,
        end: true,
    },
    {
        path: "/labs/cart/payment",
        component: CartPayment,
        end: true,
    },
    {
        path: "/labs/cart/order-payment",
        component: CartPayment,
        end: true,
    },
    {
        path: "/labs/cart/payment/recharge",
        component: RechargeWallet,
        end: true,
    },
    {
        path: "/labs/checkout",
        component: CartPayment,
        end: true,
    },
    {
        path: "/labs/cart/checkout/success",
        end: true,
        component: OrderSuccessPage,
    },
    {
        path: "/corporate/labs",
        component: LabsBookingPage,
        children: [
            {
                path: "patient",
                component: PatientSelection,
            },
            {
                path: "packages",
                component: PackageSelection,
            },
            {
                path: "progress/:test_id",
                component: TestDetails,
            },
            {
                path: "review",
                component: BookingReview,
            },
        ],
    },
    {
        path: "/corporate/self-serve",
        component: SelfServeContainer,
        children: [
            {
                path: "login",
                component: SelfServeLogin,
            },
            {
                path: "booking",
                component: SelfServeLeadPage,
            },
            {
                path: "booking-success",
                component: SelfServeBookingSuccess,
            },
        ],
    },
    {
        path: "/partnerships",
        component: CorporateLandingPageContainer,
        children: [
            {
                path: "",
                component: CorporateLandingPage,
            },
            {
                path: "products/:productName",
                component: BusinessVerticalPage,
            },
        ],
    },
    {
        path: "/vaccination-at-home",
        component: VaccinationPageContainer,
        children: [
            {
                path: "",
                component: VaccinationHomePage,
            },
        ],
    },
    {
        path: "/insights-supplement",
        end: true,
        component: InsightsSupplement,
    },

    {
        path: "/health-record",
        end: true,
        component: HealthRecordsPage,
    },
    {
        path: "/health-record/:patient_id",
        end: true,
        component: HealthRecordsPage,
    },
    {
        path: "/trust",
        component: CampaignLandingPageContainer,
        children: [
            {
                path: "sawaaluthao",
                component: TrustLandingPage,
            },
        ],
    },
    {
        path: "/survey-form",
        component: LeadGenPage,
        end: true,
    },
    { path: "/dweb-login", component: ErrorComponent, end: true },
    { path: "*", component: ErrorPage, end: true },
]

const getRoutes = () => routes

/**
 * during SSR, staticRouter needs to match current route for which we use useRoutes which is
 * not required in createBrowserRouter on the client side
 */
export const ServerRoutes = ({ code, error }) => {
    return useRoutes(preparedRoutes({ code, error }))
}

/**
 * Making the routes array compatible with the format accepted by createBrowserRouter
 * API on the client side
 */
export const preparedRoutes = ({ code }) => {
    const getPreparedRoutes = (routes) => {
        return routes.map((route, index) => {
            const Component = route.component
            const pageType = route.staticPageType ? route.staticPageType : null
            const routeToRender = {
                ...route,
                element: (
                    <ErrorBoundary>
                        <Component pageType={pageType} key={index} />
                    </ErrorBoundary>
                ),
            }
            if (route.children) {
                routeToRender.children = getPreparedRoutes(route.children)
            }
            return routeToRender
        })
    }

    return [
        {
            element: <App code={code} />,
            children: getPreparedRoutes(routes),
        },
    ]
}

export default getRoutes
